import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import config from "../../config/config";

const inputStyle = "block w-full px-4 py-3 border border-slate-200 rounded-2xl bg-slate-50 text-slate-900 font-bold focus:ring-4 focus:ring-blue-100 outline-none transition-all placeholder:text-slate-300";
const labelStyle = "block text-[12px] font-black text-slate-500 uppercase mb-2 ml-1 tracking-wider";

export default function NewPayment() {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSup, setSelectedSup] = useState({});
  const [penalty, setPenalty] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    pay_type: "",
    no_of_animal: "0",
    percentage: "0",
    pay_amount: "",
    remarks: ""
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchSup = async () => {
      try {
        const res = await fetch(`${config.baseUrl}/api/supplierDetails/supplier`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        setSuppliers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching suppliers:", err);
      }
    };
    fetchSup();
  }, []);

  // ── Computed Values ──
  const total_paid = parseFloat(selectedSup.total_paid) || 0;
  const balance = parseFloat(selectedSup.running_balance) || 0;
  const agreedRate = parseFloat(selectedSup.ageed_amt) || 0;
  const total_advance = parseFloat(selectedSup.total_advance) || 0;

  const noOfAnimals = parseInt(formData.no_of_animal) || 0;
  const percentage = parseFloat(formData.percentage) || 0;
  const penaltyAmt = parseFloat(penalty) || 0;
  const discountAmt = parseFloat(discount) || 0;

  // ── 🧮 MATHEMATICS LOGIC ──
  
  // 1. Total Payment = Animals * Rate
  const totalPayment = noOfAnimals * agreedRate;
  
  // 2. Payable Amount = Total Payment - Total Paid + Total Advance
  const payableAmount = parseFloat((totalPayment - total_paid + total_advance).toFixed(2));
  
  // 3. Net Payable = Payable Amount - Penalty + Discount
  const finalNetPayable = parseFloat((payableAmount - penaltyAmt + discountAmt).toFixed(2));

  // Partial Payment fallback math
  const partialPaying = parseFloat((totalPayment * (percentage / 100)).toFixed(2));
  const transactionRemaining = parseFloat((totalPayment - partialPaying).toFixed(2));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updated = { ...formData, [name]: value };

    if (name === "pay_type") {
      updated = { ...updated, pay_amount: "", no_of_animal: "0", percentage: "0" };
      setPenalty(0);
      setDiscount(0);
    }

    if (updated.pay_type === "Pay" && (name === "no_of_animal" || name === "percentage")) {
      const animals = parseInt(name === "no_of_animal" ? value : updated.no_of_animal) || 0;
      const perc = parseFloat(name === "percentage" ? value : updated.percentage) || 0;
      const total = animals * agreedRate;
      updated.pay_amount = (total * (perc / 100)).toFixed(2);
    }

    // Live state tracing window for Final Settlement Type
    if (updated.pay_type === "Final") {
      const animals = parseInt(name === "no_of_animal" ? value : updated.no_of_animal) || 0;
      const currentPenalty = parseFloat(name === "penalty_field" ? value : penalty) || 0;
      const currentDiscount = parseFloat(name === "discount_field" ? value : discount) || 0;
      
      if (name === "penalty_field") setPenalty(currentPenalty);
      if (name === "discount_field") setDiscount(currentDiscount);
      
      const liveTotalPayment = animals * agreedRate;
      const livePayableAmount = liveTotalPayment - total_paid + total_advance;
      const liveNetPayable = livePayableAmount - currentPenalty + currentDiscount;
      
      updated.pay_amount = liveNetPayable.toFixed(2);
    }

    setFormData(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSup.sup_id) { alert("Please select a supplier."); return; }
    if (!formData.pay_type) { alert("Please select a payment type."); return; }
    if (formData.pay_type === "Final" && isNaN(finalNetPayable)) { alert("Calculation error."); return; }

    setLoading(true);
    const token = localStorage.getItem('token');

    const base = {
      sup_id: selectedSup.sup_id,
      cat_id: selectedSup.cat_id,
      sub_cat_id: selectedSup.sub_cat_id,
      remarks: formData.remarks,
      pay_type: formData.pay_type,
    };

    try {
      let payloads = [];

      if (formData.pay_type === "Adv") {
        payloads = [{
          ...base,
          total_amount: formData.pay_amount,
          pay_amount: formData.pay_amount,
          no_of_animal: 0,
          percentage: 0,
          remaining_amount: "0.00",
        }];
      } 
      else if (formData.pay_type === "Pay") {
        payloads = [{
          ...base,
          no_of_animal: formData.no_of_animal,
          percentage: formData.percentage,
          total_amount: totalPayment.toFixed(2),
          pay_amount: partialPaying.toFixed(2),
          remaining_amount: transactionRemaining.toFixed(2),
        }];
      } 
    else if (formData.pay_type === "Final") {
  // 👉 Final payment structured payload with safe fallback validation keys
  payloads = [{
    ...base,
    total_amount: totalPayment.toFixed(2),
    no_of_animal: formData.no_of_animal,
    percentage: 100,                     // 👈 Yeh line add karein taake validation pass ho jaye
    remaining_amount: "0.00",            // 👈 Safe fallback for backend field checks
    adv_pay: total_paid.toFixed(2),      
    deposit: total_advance.toFixed(2),   
    panelty: penaltyAmt.toFixed(2),       
    discount: discountAmt.toFixed(2),
    net_pay: finalNetPayable.toFixed(2),   
    pay_amount: finalNetPayable.toFixed(2), 
    created_at: new Date().toISOString().split('T')[0] 
  }];
}

      const responses = await Promise.all(payloads.map(p =>
        fetch(`${config.baseUrl}/api/SupplierDetails/addsupplierPayment`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(p)
        })
      ));

      let allOk = true;
      let backendError = "";

      for (let res of responses) {
        if (!res.ok) {
          allOk = false;
          const errorData = await res.json();
          backendError = errorData.message || errorData.error || "Server error";
          break;
        }
      }

      if (allOk) {
        alert("Transaction Posted Successfully!");
        navigate("/supplier/payment");
      } else {
        alert(`Failed: ${backendError}`);
      }

    } catch (err) {
      console.error(err);
      alert("Network Error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="p-6 lg:p-12 flex justify-center">
      <div className="w-full max-w-3xl">
        <div className="mb-8 flex items-center space-x-3">
          <div className="h-10 w-2 bg-indigo-800 rounded-full" />
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">
            Payment <span className="text-indigo-700">Portal</span>
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="bg-indigo-50/50 p-8 rounded-[2rem] shadow-2xl border border-slate-100 space-y-8">
          <div>
            <label className={labelStyle}>Select Supplier Account</label>
            <select
              className={inputStyle}
              onChange={(e) => {
                setSelectedSup(suppliers.find(s => s.sup_id == e.target.value) || {});
                setFormData({ pay_type: "", no_of_animal: "0", percentage: "0", pay_amount: "", remarks: "" });
                setPenalty(0);
                setDiscount(0);
              }}
            >
              <option value="">-- Choose Account --</option>
              {suppliers.map(s => <option key={s.sup_id} value={s.sup_id}>{s.sup_name}</option>)}
            </select>
          </div>

          {selectedSup.sup_id && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-5 bg-slate-900 rounded-2xl text-white">
                <small className="block text-blue-400 font-bold uppercase text-[9px] mb-1">Agreed Rate / Animal</small>
                <div className="flex items-baseline space-x-1">
                  <span className="text-xs text-slate-400">Rs.</span>
                  <span className="text-2xl font-black">{agreedRate.toLocaleString()}</span>
                </div>
              </div>
              <div className="p-5 bg-blue-50 border-2 border-blue-100 rounded-2xl">
                <small className="block text-blue-500 font-bold uppercase text-[9px] mb-1">Total Paid</small>
                <div className="flex items-baseline space-x-1 text-blue-600">
                  <span className="text-xs">Rs.</span>
                  <span className="text-2xl font-black">{total_paid.toLocaleString()}</span>
                </div>
              </div>
              <div className="p-5 bg-amber-50 border-2 border-amber-100 rounded-2xl">
                <small className="block text-amber-600 font-bold uppercase text-[9px] mb-1">Total Advance</small>
                <div className="flex items-baseline space-x-1 text-amber-700">
                  <span className="text-xs">Rs.</span>
                  <span className="text-2xl font-black">{total_advance.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className={labelStyle}>Payment Type</label>
            <select name="pay_type" value={formData.pay_type} onChange={handleInputChange} className={inputStyle}>
              <option value="">Select Type</option>
              <option value="Adv">Security Deposit</option>
              <option value="Pay">Partial Payment</option>
              <option value="Final">Final Settlement</option>
            </select>
          </div>

          {/* 1. SECURITY DEPOSIT */}
          {formData.pay_type === "Adv" && (
            <div className="p-6 bg-indigo-50 rounded-[1.5rem] border-2 border-dashed border-indigo-200 space-y-4">
              <label className={labelStyle}>Deposit Amount</label>
              <div className="relative">
                <input name="pay_amount" type="number" value={formData.pay_amount} onChange={handleInputChange} className={inputStyle} placeholder="0.00" />
                <span className="absolute right-4 top-3 text-slate-400 font-bold">PKR</span>
              </div>
            </div>
          )}

          {/* 2. PARTIAL PAYMENT */}
          {formData.pay_type === "Pay" && (
            <div className="p-6 bg-blue-50 rounded-[1.5rem] border-2 border-dashed border-blue-200 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelStyle}>No. of Animals</label>
                  <input type="number" name="no_of_animal" value={formData.no_of_animal} onChange={handleInputChange} className={inputStyle} />
                </div>
                <div>
                  <label className={labelStyle}>Payable %</label>
                  <input type="number" name="percentage" value={formData.percentage} onChange={handleInputChange} className={inputStyle} max="100" />
                </div>
              </div>
              {noOfAnimals > 0 && percentage > 0 && (
                <div className="grid grid-cols-3 gap-3 pt-1">
                  <div className="p-4 bg-white border rounded-2xl">
                    <small className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Total Payment</small>
                    <span className="text-lg font-black text-slate-700">{totalPayment.toLocaleString()}</span>
                  </div>
                  <div className="p-4 bg-blue-100 border rounded-2xl">
                    <small className="block text-[9px] font-black text-blue-600 uppercase tracking-widest mb-1">Paying Now</small>
                    <span className="text-lg font-black text-blue-700">{partialPaying.toLocaleString()}</span>
                  </div>
                  <div className="p-4 bg-orange-50 border rounded-2xl">
                    <small className="block text-[9px] font-black text-orange-500 uppercase tracking-widest mb-1">Remaining</small>
                    <span className="text-lg font-black text-orange-600">{transactionRemaining.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 3. FINAL SETTLEMENT */}
          {formData.pay_type === "Final" && (
            <div className="p-6 bg-red-50 rounded-[1.5rem] border-2 border-dashed border-red-200 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelStyle}>No. of Animals</label>
                  <input 
                    type="number" 
                    name="no_of_animal" 
                    value={formData.no_of_animal} 
                    onChange={handleInputChange} 
                    className={inputStyle} 
                  />
                </div>
                <div>
                  <label className={labelStyle}>Penalty (-)</label>
                  <input 
                    name="penalty_field" 
                    type="number" 
                    value={penalty || ""}
                    onChange={handleInputChange} 
                    className="block w-full px-4 py-3 border border-red-200 rounded-2xl bg-white text-red-600 font-bold outline-none" 
                    placeholder="0.00" 
                  />
                </div>
                <div>
                  <label className={labelStyle}>Discount (+)</label>
                  <input 
                    name="discount_field" 
                    type="number" 
                    value={discount || ""}
                    onChange={handleInputChange} 
                    className="block w-full px-4 py-3 border border-emerald-200 rounded-2xl bg-white text-emerald-600 font-bold outline-none" 
                    placeholder="0.00" 
                  />
                </div>
              </div>

              {/* Formula Layout Display Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center text-xs">
                <div className="p-3 bg-white rounded-xl border border-slate-200">
                  <small className="block text-[9px] font-black text-slate-400 uppercase mb-0.5">Total Payment</small>
                  <span className="font-black text-slate-800">{totalPayment.toLocaleString()}</span>
                </div>
                <div className="p-3 bg-slate-100 rounded-xl border border-slate-200">
                  <small className="block text-[9px] font-black text-slate-500 uppercase mb-0.5">Payable Amount</small>
                  <span className="font-black text-slate-700">{payableAmount.toLocaleString()}</span>
                </div>
                <div className="p-3 bg-red-100 text-red-700 rounded-xl">
                  <small className="block text-[9px] font-black text-red-500 uppercase mb-0.5">Penalty (-)</small>
                  <span className="font-black">{penaltyAmt.toLocaleString()}</span>
                </div>
                <div className="p-3 bg-emerald-600 rounded-xl text-white shadow-md col-span-2 md:col-span-1">
                  <small className="block text-[9px] font-bold text-emerald-200 uppercase mb-0.5">Net Payable (+Disc)</small>
                  <span className="font-black text-sm">{finalNetPayable.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className={labelStyle}>Transaction Remarks</label>
            <textarea name="remarks" value={formData.remarks} onChange={handleInputChange} className={inputStyle} rows="3" placeholder="Notes..." />
          </div>

          <button type="submit" disabled={loading || !selectedSup.sup_id} className="w-full py-4 bg-indigo-800 text-white font-black rounded-2xl hover:bg-indigo-700 disabled:opacity-50 uppercase tracking-widest transition-all">
            {loading ? "Processing..." : "Submit Transaction"}
          </button>
        </form>
      </div>
    </main>
  );
}